import { useStore } from 'react-redux';
import { useAppDispatch } from '@store/hooks';
import { useOpenDriverConversationMutation } from '@store/rtk/driverChat';
import {
  failPanelSeed,
  openPanelWithSeed,
  rememberChatDriver,
} from '@store/slices/driverChat/driverChatSlice';
import type { RootState } from '@store/store';
import { mergeChatSeed } from '@component/DriverChat/helpers';
import { DriverChatSeed } from '@component/DriverChat/types';

/**
 * Starts (or resumes) the conversation for a segment: opens the popup at once
 * with a loading thread, then POST /conversations fills it in. If the server
 * refuses, the popup shows the error with a retry.
 */
export const useStartDriverChat = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [openConversation, { isLoading }] = useOpenDriverConversationMutation();

  const start = async (seed: DriverChatSeed) => {
    // Keep the driver's phone/photo from the segment on screen, so the thread
    // still has them when it is reopened later from the panel.
    dispatch(
      rememberChatDriver({
        segmentId: seed.segmentId,
        phone: seed.driverPhone,
        image: seed.driverImage,
      })
    );
    dispatch(openPanelWithSeed({ ...seed, conversationId: undefined }));
    // The popup may have been closed or switched to another ride meanwhile.
    const isStillWaiting = () => {
      const { isPanelOpen, panelSeed } = store.getState().driverChat;
      return (
        isPanelOpen &&
        panelSeed?.segmentId === seed.segmentId &&
        panelSeed.conversationId == null
      );
    };
    try {
      const conversation = await openConversation(seed.segmentId).unwrap();
      if (!isStillWaiting()) return;
      dispatch(
        openPanelWithSeed({
          ...mergeChatSeed(conversation, seed),
          conversationId: conversation.conversationId,
          conversationStatus: conversation.conversationStatus,
        })
      );
    } catch {
      if (isStillWaiting()) dispatch(failPanelSeed());
    }
  };

  return { start, isStarting: isLoading };
};
